import BodyText from '@lyra/core/components/BodyText'
import Section, { SectionProps } from '@lyra/core/components/Section'
import Table, { createColumnHelper, TableData } from '@lyra/core/components/Table'
import formatPercentage from '@lyra/core/utils/formatPercentage'
import formatUSD from '@lyra/core/utils/formatUSD'
import TokenIcon from '@lyra/web/components/common/TokenIcon'
import { MARKET_ICON_SIZE } from '@lyra/web/constants/layout'
import { PageId } from '@lyra/web/constants/pages'
import { CollateralId, DepositTokenId, TokenId } from '@lyra/web/constants/tokens'
import useAuth from '@lyra/web/hooks/useAuth'
import useCollateralData from '@lyra/web/hooks/useCollateralData'
import useCollaterals from '@lyra/web/hooks/useCollaterals'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import { getPagePath } from '@lyra/web/utils/pages'
import { formatCollateralBalance, formatTokenName, formatTokenSymbol } from '@lyra/web/utils/tokens'
import { useRouter } from 'next/navigation'
import { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

import DepositButton from '../common/DepositButton'
import SignInButton from '../common/SignInButton'
import SubaccountsDropdown from '../common/SubaccountsDropdown'
import TransferButton from '../common/TransferButton'

type LendTableData = TableData<{
  collateralId: CollateralId
  totalSupply: number
  tvl: number
  supplyApy: number
  balance: number
  balanceValue: number
}>

type Props = {
  showSubaccountSwitch?: boolean
  showConnectPrompt?: boolean
} & SectionProps

const LendSection = ({ showSubaccountSwitch, showConnectPrompt, ...sectionProps }: Props) => {
  const { subaccount, subaccountData, subaccounts, switchSubaccount } = useSubaccount()

  const { data, isLoading, error } = useCollateralData(CollateralId.USDC)

  const usdcCollateral = useCollaterals()[CollateralId.USDC]

  const columnHelper = createColumnHelper<LendTableData>()

  const router = useRouter()

  const rows: LendTableData[] = useMemo(() => {
    if (!data) {
      return []
    }
    const supplyApy = +data.supply_apy
    const totalSupply = +data.total_supply
    const spotPrice = usdcCollateral.spotPrice
    const tvl = totalSupply * spotPrice

    const balance = subaccountData
      ? subaccountData.collateralBalances[CollateralId.USDC].balance
      : 0
    const balanceValue = balance * spotPrice

    return [
      {
        collateralId: CollateralId.USDC,
        totalSupply,
        tvl,
        supplyApy,
        balance,
        balanceValue,
        onPress: () =>
          router.push(getPagePath({ page: PageId.Collateral, collateralId: CollateralId.USDC })),
      },
    ]
  }, [usdcCollateral, data, subaccountData, router])

  const columns = useMemo(
    () => [
      columnHelper.accessor('collateralId', {
        header: 'Collateral',
        meta: {
          minWidth: 180,
        },
        cell: ({
          row: {
            original: { collateralId },
          },
        }) => {
          return (
            <XStack gap="$1" alignItems="center">
              <TokenIcon symbol={collateralId} size={MARKET_ICON_SIZE} />
              <YStack>
                <BodyText>{formatTokenSymbol(collateralId)}</BodyText>
                <BodyText color="secondary">{formatTokenName(collateralId)}</BodyText>
              </YStack>
            </XStack>
          )
        },
      }),
      columnHelper.accessor('totalSupply', {
        header: 'Total Supply',
        meta: {
          minWidth: 100,
        },
        cell: ({
          row: {
            original: { totalSupply, tvl, collateralId },
          },
        }) => {
          return (
            <YStack>
              <BodyText>
                {formatCollateralBalance(totalSupply, collateralId, { truncate: true })}
              </BodyText>
              <BodyText color="secondary">{formatUSD(tvl, { dps: 0 })}</BodyText>
            </YStack>
          )
        },
      }),
      columnHelper.accessor('supplyApy', {
        header: 'Supply APY',
        meta: {
          minWidth: 100,
        },
        cell: ({
          row: {
            original: { supplyApy },
          },
        }) => {
          return (
            <BodyText color={supplyApy > 0 ? 'green' : 'primary'}>
              {formatPercentage(supplyApy, { showSign: true })}
            </BodyText>
          )
        },
      }),
      columnHelper.accessor('balance', {
        header: 'Balance',
        meta: {
          minWidth: 100,
        },
        cell: ({
          row: {
            original: { balance, balanceValue, collateralId },
          },
        }) => {
          return (
            <YStack>
              <BodyText color={balance ? 'primary' : 'secondary'}>
                {balance ? formatCollateralBalance(balance, collateralId) : '—'}
              </BodyText>
              {balanceValue ? (
                <BodyText color="secondary">{formatUSD(balanceValue)}</BodyText>
              ) : null}
            </YStack>
          )
        },
      }),
      columnHelper.accessor('action', {
        header: 'Action',
        meta: {
          minWidth: 170,
          maxWidth: 170,
          justifyContent: 'flex-end',
        },
        cell: () => {
          return (
            <XStack gap="$1" onPress={(e) => e.stopPropagation()}>
              <DepositButton defaultDepositToken={DepositTokenId.USDC} isCta={false} />
              <TransferButton
                defaultToSubaccountId={subaccount?.subaccount_id}
                asset={TokenId.USDC}
              />
            </XStack>
          )
        },
      }),
    ],
    [columnHelper, subaccount]
  )

  const { isAuthenticated } = useAuth()

  return (
    <Section {...sectionProps}>
      <Section.Header
        title="Lend USDC"
        rightContent={
          Object.values(subaccounts).length > 1 && subaccount && showSubaccountSwitch ? (
            <SubaccountsDropdown
              subaccountId={subaccount.subaccount_id}
              onSelect={switchSubaccount}
            />
          ) : null
        }
      />
      {isAuthenticated ? (
        <Section.XStack>
          <TransferButton
            label="Supply USDC"
            defaultFromSubaccountId="funding"
            toSubaccountId={subaccount?.subaccount_id}
            asset={TokenId.USDC}
            color="cta"
            size="lg"
            useCase="supply"
            minWidth={140}
          />
        </Section.XStack>
      ) : showConnectPrompt ? (
        <Section.XStack>
          <SignInButton isCta size="lg" minWidth={140} />
        </Section.XStack>
      ) : null}
      <Section.Table>
        <Table
          size="lg"
          columns={columns}
          data={rows}
          emptyMessage={
            isLoading ? '...' : error ? 'Failed to load balances' : 'Something went wrong'
          }
        />
      </Section.Table>
    </Section>
  )
}

export default LendSection
